import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { PlayerGameScoreProjectionGrade } from '@sorare/core/src/__generated__/globalTypes';
import { HexagonShape } from '@sorare/core/src/atoms/icons/HexagonShape';
import { BrandS, BrandXXS } from '@sorare/core/src/atoms/typography';
import { CornerBadge } from '@sorare/core/src/components/CornerBadge';
import { type Unit } from '@sorare/core/src/lib/style';
import type { Color } from '@sorare/core/src/style/types';
import { css } from '@sorare/css';

import type { Grade_playerGameScoreProjection } from './__generated__/index.graphql.ts';

const gradeColors: Record<PlayerGameScoreProjectionGrade, Color> = {
  [PlayerGameScoreProjectionGrade.A]: 'var(--c-score-high)',
  [PlayerGameScoreProjectionGrade.B]: 'var(--c-score-mediumHigh)',
  [PlayerGameScoreProjectionGrade.C]: 'var(--c-score-medium)',
  [PlayerGameScoreProjectionGrade.D]: 'var(--c-score-mediumLow)',
  [PlayerGameScoreProjectionGrade.E]: 'var(--c-score-low)',
  [PlayerGameScoreProjectionGrade.F]: 'var(--c-score-veryLow)',
};

const label = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const HIGH_RELIABILITY_THRESHOLD = 5000;

const getColor = (
  grade?: PlayerGameScoreProjectionGrade,
  reliabilityBasisPoints?: number
): Color => {
  if ((reliabilityBasisPoints || 0) > HIGH_RELIABILITY_THRESHOLD && grade)
    return gradeColors[grade];
  return 'var(--c-nd-600-opaque)';
};

export const Grade = ({
  projection,
  size = 4,
  variant = 'hexagon',
}: {
  projection: Nullable<Grade_playerGameScoreProjection>;
  size?: Unit;
  variant?: 'hexagon' | 'cornerBadge';
}) => {
  const { grade, reliabilityBasisPoints } = projection || {};

  const ActualLabel = size < 3 ? BrandXXS : BrandS;

  if (variant === 'cornerBadge') {
    return (
      <CornerBadge color={getColor(grade, reliabilityBasisPoints)}>
        <BrandXXS>{grade || '-'}</BrandXXS>
      </CornerBadge>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <HexagonShape
        size={size}
        fill={getColor(grade, reliabilityBasisPoints)}
      />
      <ActualLabel className={label} color="var(--c-onBase-light)">
        {grade || '-'}
      </ActualLabel>
    </div>
  );
};

Grade.fragments = {
  projection: gql`
    fragment Grade_playerGameScoreProjection on PlayerGameScoreProjection {
      id
      grade
      reliabilityBasisPoints
    }
  ` as TypedDocumentNode<Grade_playerGameScoreProjection>,
};
